import { Shipment, ShipmentApiResponse, DisplayShipmentData, TrackingEvent, PaymentStatus } from './types';

// ====================
// UTILITY FUNCTIONS
// ====================

// Map API response to our Shipment interface
export const mapApiResponseToShipment = (response: ShipmentApiResponse): Shipment => {
    // Determine if we're using a regular package or combined package
    const isUsingCombinedPackage = !response.package && response.combinedPackage;

    // Get the active package data (either regular or combined)
    const activePackage: any = isUsingCombinedPackage ? response.combinedPackage : response.package;

    if (!activePackage) {
        throw new Error('No package data available');
    }

    // Calculate total value from package items
    let totalValue = 0;

    // Handle items differently for combined packages vs regular packages
    let packageItems: any[] = [];

    // If it's a regular package with items
    if (activePackage.item) {
        packageItems = activePackage.item.map((item: any) => ({
            id: item.id.toString(),
            name: item.name,
            price: parseFloat(item.price),
            quantity: item.quantity,
            currency: item.currency || 'USD',
            description: item.description || undefined,
            category: item.category || undefined,
        }));
    }
    // If it's a combined package, we need to flatten all items from all packages
    else if (isUsingCombinedPackage && activePackage.package && Array.isArray(activePackage.package)) {
        // Flatten all items from all packages in the combined package
        packageItems = activePackage.package.flatMap((pkg: any) =>
            pkg.item ? pkg.item.map((item: any) => ({
                id: item.id.toString(),
                name: item.name,
                price: parseFloat(item.price),
                quantity: item.quantity,
                currency: item.currency || 'USD',
                description: item.description || undefined,
                category: item.category || undefined,
            })) : []
        );
    }

    // Calculate total value from items
    interface PackageItem {
        id: number;
        name: string;
        price: string;
        quantity: number;
        currency?: string;
        description?: string;
        category?: string;
    }

    if (isUsingCombinedPackage && activePackage.package && Array.isArray(activePackage.package)) {
        // For combined packages, sum up values from all packages' items
        totalValue = activePackage.package.reduce((packageSum: number, pkg: any) => {
            if (pkg.item && pkg.item.length > 0) {
                // Sum up values for each item in this package
                const packageItemValue = pkg.item.reduce(
                    (sum: number, item: PackageItem) => sum + (parseFloat(item.price) * item.quantity),
                    0
                );
                return packageSum + packageItemValue;
            }
            // If no items, use the package's custom value
            return packageSum + (pkg.custom_value ? parseFloat(pkg.custom_value) : 0);
        }, 0);
    } else if (activePackage.item && activePackage.item.length > 0) {
        // For regular packages, calculate from its items
        totalValue = activePackage.item.reduce(
            (sum: number, item: PackageItem) => sum + (parseFloat(item.price) * item.quantity),
            0
        );
    } else if (activePackage.custom_value) {
        // Fallback to custom_value if no items
        totalValue = parseFloat(activePackage.custom_value);
    }

    // Map the state to our status type based on API state and tracking details
    let status: 'processing' | 'shipped' | 'delivered';

    // Check tracking details for status
    const isDelivered = response.trackingDetails?.some(
        detail => detail.waypoint === "DELIVERED"
    );

    const isShipped = response.trackingDetails?.some(
        detail => ["HANDED_OVER_TO_AIRLINE", "ARRIVED_AT_DESTINATION", "CUSTOMS_CLEARED"].includes(detail.waypoint)
    );

    // Map API state to our status
    if (isDelivered) {
        status = 'delivered';
    } else if (isShipped) {
        status = 'shipped';
    } else if (response.state === 'APPROVED') {
        status = 'processing';
    } else {
        status = 'processing'; // Default fallback
    }

    // Sort tracking details by timestamp (newest first)
    const sortedTrackingDetails = response.trackingDetails
        ? [...response.trackingDetails].sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )
        : [];

    // Format tracking waypoint for display
    const formatWaypoint = (waypoint: string) => {
        return waypoint
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Find estimated delivery date (3 days after HANDED_OVER_TO_AIRLINE if not delivered)
    const shippedEvent = sortedTrackingDetails.find(d => d.waypoint === "HANDED_OVER_TO_AIRLINE");
    let estimatedDelivery: string | undefined = undefined;

    if (shippedEvent && !isDelivered) {
        const estDate = new Date(shippedEvent.timestamp);
        estDate.setDate(estDate.getDate() + 3);
        estimatedDelivery = estDate.toISOString();
    }

    // Generate tracking number
    const trackingNumber = `TR-${response.order_id}`;

    // Calculate package value from package custom_value
    const packageValue = parseFloat(activePackage.custom_value || '0');

    return {
        shipment_id: response.id,
        id: response.order_id,
        name: `Shipment ${response.order_id}`,
        status: status,
        createdDate: response.created_at,
        deliveredDate: sortedTrackingDetails.find(d => d.waypoint === "DELIVERED")?.timestamp,
        shippedDate: shippedEvent?.timestamp,
        trackingNumber: trackingNumber,
        carrier: isUsingCombinedPackage ? "OTL Express" : "OTL Standard",
        estimatedDelivery: estimatedDelivery,
        totalWeight: parseFloat(activePackage.weight),
        totalValue: totalValue,
        packageCount: activePackage.quantity || 1,
        destination: {
            name: `${response.user.first_name} ${response.user.last_name}`,
            street: response.reciver_Address.address,
            city: response.reciver_Address.city,
            state: response.reciver_Address.state,
            postalCode: response.reciver_Address.zip,
            country: response.reciver_Address.country,
            phone: response.user.tel ?? '',
        },
        packages: isUsingCombinedPackage && activePackage.package ?
            // Map each package in the combined package
            activePackage.package.map((pkg: any) => ({
                id: pkg.id.toString(),
                name: pkg.description || `Package ${pkg.id}`,
                description: pkg.description || 'No description available',
                weight: parseFloat(pkg.weight),
                dimensions: {
                    length: parseFloat(pkg.length),
                    width: parseFloat(pkg.width),
                    height: parseFloat(pkg.height),
                },
                value: parseFloat(pkg.custom_value || '0'),
                items: pkg.item ? pkg.item.map((item: any) => ({
                    id: item.id.toString(),
                    name: item.name,
                    price: parseFloat(item.price),
                    quantity: item.quantity,
                    currency: item.currency || 'USD',
                    description: item.description || undefined,
                    category: item.category || undefined,
                })) : [],
            })) :
            // Single package case
            [{
                id: activePackage.id.toString(),
                name: activePackage.description || `Package ${activePackage.id}`,
                description: activePackage.description || 'No description available',
                weight: parseFloat(activePackage.weight),
                dimensions: {
                    length: parseFloat(activePackage.length),
                    width: parseFloat(activePackage.width),
                    height: parseFloat(activePackage.height),
                },
                value: packageValue,
                items: packageItems,
            }],
        events: sortedTrackingDetails.map(detail => ({
            date: detail.timestamp,
            status: formatWaypoint(detail.waypoint),
            location: getLocationForWaypoint(detail.waypoint, response.reciver_Address.city),
            description: getDescriptionForWaypoint(detail.waypoint),
        })),
        cost: {
            shipping: parseFloat(activePackage.shipping_cost),
            insurance: 0, // Not provided in API
            consolidation: isUsingCombinedPackage ? 10 : 0, // Fee for combined packages
            tax: calculateTax(parseFloat(response.cost)),
            total: parseFloat(response.cost),
            paymentStatus: response.pay_state as PaymentStatus,
            url: response.pay_url || '',
        },
    };
};

// Helper function to get a descriptive location based on the waypoint
const getLocationForWaypoint = (waypoint: string, destinationCity: string): string => {
    switch (waypoint) {
        case "ORDER_CREATED":
        case "PROCESSING_AT_WAREHOUSE":
        case "READY_TO_SHIP":
            return "Origin Warehouse";
        case "HANDED_OVER_TO_AIRLINE":
            return "Origin Airport";
        case "ARRIVED_AT_DESTINATION":
            return `${destinationCity} Airport`;
        case "HELD_BY_CUSTOMS":
        case "CUSTOMS_CLEARED":
            return `${destinationCity} Customs`;
        case "PROCESSING_AT_LOCAL_WAREHOUSE":
            return `${destinationCity} Distribution Center`;
        case "DELIVERED":
            return destinationCity;
        default:
            return "Unknown Location";
    }
};

// Helper function to get a descriptive text for each waypoint
const getDescriptionForWaypoint = (waypoint: string): string => {
    switch (waypoint) {
        case "ORDER_CREATED":
            return "Shipment order has been created and confirmed.";
        case "PROCESSING_AT_WAREHOUSE":
            return "Your shipment is being processed at our warehouse.";
        case "READY_TO_SHIP":
            return "Your shipment is packed and ready to be handed over for shipping.";
        case "HANDED_OVER_TO_AIRLINE":
            return "Your shipment has been handed over to the airline for transportation.";
        case "ARRIVED_AT_DESTINATION":
            return "Your shipment has arrived at the destination airport.";
        case "HELD_BY_CUSTOMS":
            return "Your shipment is being inspected by customs authorities.";
        case "CUSTOMS_CLEARED":
            return "Your shipment has been cleared by customs and is proceeding to delivery.";
        case "PROCESSING_AT_LOCAL_WAREHOUSE":
            return "Your shipment is at the local distribution center being prepared for final delivery.";
        case "DELIVERED":
            return "Your shipment has been successfully delivered to the destination address.";
        default:
            return "Status update for your shipment.";
    }
};

// Calculate tax at 5% of shipping cost
const calculateTax = (total: number): number => {
    return total * 0.05;
};


// Format utility functions
export const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };

        return date.toLocaleDateString('en-US', options).replace(',', '');
    } catch (e) {
        return dateString;
    }
};

/**
 * Get the appropriate color for a status badge
 * @param status - Shipment status
 * @returns Color for the badge
 */
export const getStatusBadgeColor = (status: string): string => {
    const statusLower = status?.toLowerCase() || '';

    if (statusLower.includes('delivered')) return 'green';
    if (statusLower.includes('approved')) return 'blue';
    if (statusLower.includes('transit') || statusLower.includes('shipped')) return 'indigo';
    if (statusLower.includes('processing')) return 'violet';
    if (statusLower.includes('ready')) return 'cyan';
    if (statusLower.includes('held')) return 'orange';
    if (statusLower.includes('customs')) return 'yellow';
    if (statusLower.includes('pending') || statusLower.includes('requested')) return 'blue';
    if (statusLower.includes('rejected') || statusLower.includes('failed')) return 'red';

    return 'gray';
};

/**
 * Transform tracking details into tracking events
 * @param details - Array of tracking details from API
 * @returns Array of tracking events for display
 */
export const transformTrackingDetailsToEvents = (details: Array<{ waypoint: string, timestamp: string }>): TrackingEvent[] => {
    if (!details || !Array.isArray(details)) return [];

    return details
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .map(detail => {
            const waypointToEventMap: Record<string, { status: string, description: string }> = {
                'ORDER_CREATED': {
                    status: 'Order Created',
                    description: 'Your shipment order has been created and is being prepared for processing.'
                },
                'PROCESSING_AT_WAREHOUSE': {
                    status: 'Processing at Warehouse',
                    description: 'Your package is being processed at our warehouse facility.'
                },
                'READY_TO_SHIP': {
                    status: 'Ready to Ship',
                    description: 'Your package has been prepared and is ready for shipping.'
                },
                'HANDED_OVER_TO_AIRLINE': {
                    status: 'Handed Over to Carrier',
                    description: 'Your package has been handed over to the shipping carrier.'
                },
                'ARRIVED_AT_DESTINATION': {
                    status: 'Arrived at Destination',
                    description: 'Your package has arrived at the destination country.'
                },
                'HELD_BY_CUSTOMS': {
                    status: 'Held by Customs',
                    description: 'Your package is being held for customs inspection.'
                },
                'CUSTOMS_CLEARED': {
                    status: 'Customs Cleared',
                    description: 'Your package has cleared customs and is continuing to its destination.'
                },
                'PROCESSING_AT_LOCAL_WAREHOUSE': {
                    status: 'Processing at Local Warehouse',
                    description: 'Your package has arrived at the local warehouse for final delivery preparation.'
                },
                'DELIVERED': {
                    status: 'Delivered',
                    description: 'Your package has been successfully delivered.'
                }
            };

            const eventInfo = waypointToEventMap[detail.waypoint] || {
                status: detail.waypoint.replace(/_/g, ' '),
                description: 'Package status updated.'
            };

            return {
                status: eventInfo.status,
                date: detail.timestamp,
                location: 'International Shipping', // Default location as it's not provided in the API
                description: eventInfo.description
            };
        });
};

/**
 * Calculate the total value of items in a package
 * @param items - Array of items in a package
 * @returns Total value
 */
export const calculateTotalValue = (items?: any[]): number => {
    if (!items || !Array.isArray(items)) return 0;

    return items.reduce((sum, item) => {
        const value = parseFloat(item.value || '0');
        const quantity = parseInt(item.quantity || '1', 10);
        return sum + (isNaN(value) ? 0 : value * quantity);
    }, 0);
};

/**
 * Transform the API response into a format suitable for our components
 * @param response - API response
 * @returns Formatted shipment data
 */
export const transformShipmentData = (response: ShipmentApiResponse): DisplayShipmentData => {
    if (!response) {
        throw new Error('Invalid shipment data provided');
    }

    // Select the appropriate package data (either regular package or combined package)
    const activePackage: any = response.package || response.combinedPackage;

    if (!activePackage) {
        throw new Error('No package data found in shipment');
    }

    // Calculate total value from items
    const totalValue = activePackage.custom_value ? parseFloat(activePackage.custom_value) : 0;

    // Sort tracking details by timestamp (newest first)
    const sortedTrackingDetails = response.trackingDetails
        ? [...response.trackingDetails].sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )
        : [];

    // Transform tracking details to our event format
    const events = transformTrackingDetailsToEvents(response.trackingDetails || []);

    // Determine the package status from the state field or the most recent tracking event
    let packageStatus = response.state || 'processing';
    if (sortedTrackingDetails[0] && sortedTrackingDetails[0].waypoint === 'DELIVERED') {
        packageStatus = 'delivered';
    } else if (sortedTrackingDetails.find(d => d.waypoint === 'HANDED_OVER_TO_AIRLINE')) {
        packageStatus = 'in transit';
    }

    return {
        id: response.id,
        orderId: response.order_id,
        status: packageStatus,
        events: events,
        createdDate: response.created_at,
        // If delivered, use the delivered timestamp
        deliveredDate: sortedTrackingDetails.find(d => d.waypoint === "DELIVERED")?.timestamp,
        // Use first shipping-related timestamp as shipped date
        shippedDate: sortedTrackingDetails.find(d => d.waypoint === "HANDED_OVER_TO_AIRLINE")?.timestamp,
        trackingNumber: response.order_id, // Using order_id as tracking number since it's not provided
        carrier: "International Express", // Default carrier since it's not provided
        estimatedDelivery: undefined, // Not available in the response
        totalWeight: parseFloat(activePackage.weight || '0'),
        totalValue: totalValue,
        packageCount: activePackage.quantity || 1,
        destination: {
            name: `${response.user.first_name} ${response.user.last_name}`,
            street: response.reciver_Address?.address || '',
            city: response.reciver_Address?.city || '',
            state: response.reciver_Address?.state || '',
            postalCode: response.reciver_Address?.zip || '',
            country: response.reciver_Address?.country || '',
            phone: response.user.tel ?? '',
        },
        packages: response.combinedPackage && response.combinedPackage.package ?
            // Map each package in the combined package with its items
            response.combinedPackage.package.map((pkg: any) => ({
                id: pkg.id,
                description: pkg.description || 'No description',
                weight: parseFloat(pkg.weight || '0'),
                dimensions: {
                    length: parseFloat(pkg.length || '0'),
                    width: parseFloat(pkg.width || '0'),
                    height: parseFloat(pkg.height || '0'),
                },
                value: parseFloat(pkg.custom_value || '0'),
                items: pkg.item || [], // Keep the original items structure for each package
            })) :
            // Single package case
            [{
                id: activePackage.id,
                description: activePackage.description || 'No description',
                weight: parseFloat(activePackage.weight || '0'),
                dimensions: {
                    length: parseFloat(activePackage.length || '0'),
                    width: parseFloat(activePackage.width || '0'),
                    height: parseFloat(activePackage.height || '0'),
                },
                value: parseFloat(activePackage.custom_value || '0'),
                items: activePackage.item || [],
            }],
        cost: {
            shipping: parseFloat(response.cost || '0'),
            insurance: 0, // Not provided in the response
            tax: 0, // Not provided in the response
            other: 0, // Not provided in the response
            total: parseFloat(response.cost || '0'),
            PaymentStatus: response.pay_state as PaymentStatus,
        }
    };
};
